import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import useAuthProfile from 'shared/features/auth/authentication/providers/states/auth-profile';
import { useLazyGetActiveTenantFeatureSubscriptionsQuery } from 'shared/providers/tenant-feature-subscriptions/domain/queries/active-tenant-feature-subscriptions-queries';

import { FeatureSubscriptionType } from 'features/feature-subscriptions/models/feature-subscription-type';

export interface IActiveTenantFeatureSubscriptionsHook {
  hasActiveSubscriptionFeature: (feature: FeatureSubscriptionType) => boolean;
  hasActiveCareConnectFeature: boolean;
  activeTenantFeaturesIsLoading: boolean;
  refresh(): void;
}

const useActiveTenantFeatureSubscriptions = (): IActiveTenantFeatureSubscriptionsHook => {
  const { isAuthenticated } = useAuth();
  const { tenant } = useAuthProfile();

  const {
    data: activeTenantFeaturesData,
    refetch: refetchActiveTenantFeatures,
    isLoading: activeTenantFeaturesIsLoading,
    isError: activeTenantFeaturesIsError,
  } = useLazyGetActiveTenantFeatureSubscriptionsQuery();

  useEffect(() => {
    if (isAuthenticated) {
      refetchActiveTenantFeatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, tenant.switchActivated]);

  const hasActiveSubscriptionFeature = (feature: FeatureSubscriptionType): boolean => {
    if (activeTenantFeaturesIsError) {
      return false;
    }
    return activeTenantFeaturesData?.activeFeatures.includes(feature) ?? false;
  };

  return {
    hasActiveSubscriptionFeature: hasActiveSubscriptionFeature,
    hasActiveCareConnectFeature: hasActiveSubscriptionFeature(FeatureSubscriptionType.CareConnect) ?? false,
    refresh: refetchActiveTenantFeatures,
    activeTenantFeaturesIsLoading: activeTenantFeaturesIsLoading && !activeTenantFeaturesIsError,
  };
};

export default useActiveTenantFeatureSubscriptions;
